import { useCallback, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
// @ts-expect-error pure js
import useAuthenticatedAxios from "./use-authenticated-axios";
// @ts-expect-error pure js
import useAuth from "./use-auth.js";
import useResources from "./use-resources";
import type {
  BandeauAudience,
  BandeauInformation,
} from "../models/bandeauInformation";

const ROOT_PATH = "/bandeau-information";
const ADMIN_PATH = "/admin/bandeau-information";
const DISMISSED_STORAGE_KEY = "qalimo.bandeauInformation.dismissedId";

/** Mode de la page courante, seul élément du public que le back ne connaît pas. */
export type BandeauMode = "bailleur" | "locataire";

export interface BandeauInformationInput {
  titre: string;
  texte: string;
  lien: string;
  lien_nouvel_onglet: boolean;
  audiences: BandeauAudience[];
}

function readDismissedId(): number | null {
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    const id = raw === null ? NaN : Number(raw);
    return Number.isInteger(id) ? id : null;
  } catch {
    return null;
  }
}

function writeDismissedId(id: number) {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, String(id));
  } catch {
    // Stockage indisponible (navigation privée, quota) : le bandeau réapparaîtra
    // au prochain chargement, sans bloquer la fermeture courante.
  }
}

function isBandeauKey(key: unknown) {
  return (
    typeof key === "object" &&
    key !== null &&
    (key as { url?: string }).url === ROOT_PATH
  );
}

/**
 * Bandeau en ligne diffusé à l'utilisateur courant : le back le filtre d'après le
 * mode de la page et le type de compte, et renvoie null s'il ne lui est pas destiné.
 */
export default function useBandeauInformation(mode: BandeauMode = "bailleur") {
  const axios = useAuthenticatedAxios();
  const { auth } = useAuth();
  const {
    data: bandeau,
    error,
    isLoading,
  } = useSWR<BandeauInformation | null>(
    auth?.accessToken ? { url: ROOT_PATH, userId: auth.userId, mode } : null,
    ({ url, mode }: { url: string; mode: BandeauMode }) =>
      axios
        .get(url, { params: { mode } })
        .then((res: { data: BandeauInformation | null }) => res.data),
    {
      fallbackData: null,
      revalidateOnFocus: false,
      dedupingInterval: 5 * 60 * 1000,
    },
  );

  const [dismissedId, setDismissedId] = useState<number | null>(
    readDismissedId,
  );

  const hasContent = !!bandeau && (!!bandeau.titre || !!bandeau.texte);
  const visibleBandeau =
    hasContent && bandeau.id !== dismissedId ? bandeau : null;

  const dismiss = useCallback(() => {
    if (!bandeau) return;
    writeDismissedId(bandeau.id);
    setDismissedId(bandeau.id);
  }, [bandeau]);

  return { visibleBandeau, error, isLoading, dismiss };
}

/**
 * Administration : historique complet des publications (de la plus récente à la plus
 * ancienne) et bandeau en ligne quel que soit son public, publication et retrait.
 */
export function useBandeauInformationAdmin() {
  const axios = useAuthenticatedAxios();
  const { mutate: mutateGlobal } = useSWRConfig();
  const {
    resources: history,
    error,
    isLoading,
    mutate: mutateHistory,
  } = useResources<BandeauInformation>(ADMIN_PATH);

  const latest = history[0] ?? null;
  const current = latest && latest.retired_at === null ? latest : null;

  // Le bandeau servi aux utilisateurs dépend de leur public : on le recharge plutôt
  // que d'écrire la réponse dans le cache.
  async function refresh() {
    await Promise.all([mutateHistory(), mutateGlobal(isBandeauKey)]);
  }

  async function publish(input: BandeauInformationInput) {
    const response = await axios.post(ADMIN_PATH, input);
    await refresh();
    return response.data as BandeauInformation;
  }

  // Le retrait ne supprime rien : la dernière publication est datée `retired_at`
  // et reste dans l'historique.
  async function retire() {
    await axios.delete(ADMIN_PATH);
    await refresh();
  }

  return { history, current, error, isLoading, publish, retire };
}
